import {
  AnimatePresence,
  motion,
  useAnimationFrame,
  useMotionValue,
} from 'motion/react'

import { cn } from '@jotta/utils/css'

import type {
  PrimaryStrokeClassName,
  SecondaryStrokeClassName,
} from '../../StrokeClassNameType'

type SpinnerProps = {
  size?: 'small' | 'medium' | 'large'
  className?: string
  primaryStrokeClassName?: PrimaryStrokeClassName
  secondaryStrokeClassName?: SecondaryStrokeClassName
}

const FULL_ROTATION_MS = 2000
const DEG_PER_MS = 360 / FULL_ROTATION_MS

const Spinner = ({
  size,
  className,
  primaryStrokeClassName,
  secondaryStrokeClassName,
}: SpinnerProps) => {
  const viewBoxSize = 48
  const strokeWidth = 4
  const radius = 20
  const center = viewBoxSize / 2
  const rotate = useMotionValue(0)

  // Derive angle from time (survives unmount/remount)
  useAnimationFrame(() => {
    rotate.set((Date.now() * DEG_PER_MS) % 360)
  })

  return (
    <svg
      viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
      className={cn(
        'h-[clamp(32px,100%,64px)] w-[clamp(32px,100%,64px)] stroke-primary',
        {
          'h-14 w-14': size === 'large',
          'h-9 w-9': size === 'medium',
          'h-5 w-5': size === 'small',
        },
        primaryStrokeClassName,
        className,
      )}
    >
      {secondaryStrokeClassName && (
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          className={cn('stroke-transparent', secondaryStrokeClassName)}
        />
      )}
      <AnimatePresence>
        <motion.circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          animate={{
            strokeDasharray: ['1, 200', '90, 200', '20, 200'],
            strokeDashoffset: [0, -40, -120], // Progressively moves the dash forward
          }}
          transition={{
            duration: 1.6,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
          style={{ rotate }}
        />
      </AnimatePresence>
    </svg>
  )
}

export default Spinner
